// Hard dietary / allergy rules, deterministic, outside any model, derived from taxonomy flags.
//
// A rule either BLOCKS an ingredient (the recipe is removed, fail-closed) or raises a CAUTION
// (the recipe stays, with an explicit, visible note, e.g. "use halal-certified chicken").

import { INGREDIENTS, lineage, type Allergen, type IngredientId, ALLERGENS } from './taxonomy';

export type DietRuleId =
  | 'vegetarian' | 'vegan' | 'pescatarian'
  | 'no_pork' | 'no_beef' | 'halal' | 'gluten_free'
  | `allergy_${Allergen}`;

export interface DietRuleDef {
  id: DietRuleId;
  label: string;
  kind: 'allergy' | 'diet' | 'religious';
}

export const DIET_RULES: DietRuleDef[] = [
  ...ALLERGENS.map((a) => ({ id: `allergy_${a.id}` as DietRuleId, label: a.label, kind: 'allergy' as const })),
  { id: 'gluten_free', label: 'Gluten-free', kind: 'diet' },
  { id: 'vegetarian', label: 'Vegetarian', kind: 'diet' },
  { id: 'vegan', label: 'Vegan', kind: 'diet' },
  { id: 'pescatarian', label: 'Pescatarian', kind: 'diet' },
  { id: 'no_pork', label: 'No pork', kind: 'religious' },
  { id: 'no_beef', label: 'No beef', kind: 'religious' },
  { id: 'halal', label: 'Halal', kind: 'religious' },
];

const RULE_IDS = new Set<string>(DIET_RULES.map((r) => r.id));
export function isDietRuleId(x: unknown): x is DietRuleId {
  return typeof x === 'string' && RULE_IDS.has(x);
}

export function ruleLabel(id: DietRuleId): string {
  return DIET_RULES.find((r) => r.id === id)?.label ?? id;
}

export interface Caution {
  ingredient: IngredientId;
  rule: DietRuleId;
  note: string;
}

export interface Verdict {
  blocked: boolean;
  /** Which rule blocked it (first found), for explanations/tests. */
  blockedBy?: DietRuleId;
  cautions: Caution[];
}

/** Evaluate one ingredient against one rule. Flags are inherited through the parent chain. */
export function evaluate(id: IngredientId, rule: DietRuleId): 'ok' | 'block' | 'caution' {
  // Merge flags from the whole lineage so a child can never be "safer" than its parent.
  const chain = lineage(id).map((x) => INGREDIENTS[x]);
  const meat = chain.map((d) => d.meat).find(Boolean);
  const seafood = chain.map((d) => d.seafood).find(Boolean);
  const animal = chain.some((d) => d.animal) || !!meat || !!seafood;
  const allergens = new Set(chain.flatMap((d) => d.allergens ?? []));
  const gluten = chain.map((d) => d.gluten).find(Boolean);
  const alcohol = chain.some((d) => d.alcohol);
  const rennet = chain.some((d) => d.rennet);

  if (rule.startsWith('allergy_')) {
    return allergens.has(rule.slice('allergy_'.length) as Allergen) ? 'block' : 'ok';
  }
  switch (rule) {
    case 'vegetarian':
      if (meat || seafood) return 'block';
      return rennet ? 'caution' : 'ok';
    case 'vegan':
      return animal ? 'block' : 'ok';
    case 'pescatarian':
      return meat ? 'block' : rennet ? 'caution' : 'ok';
    case 'no_pork':
      return meat === 'pork' || meat === 'unknown' ? 'block' : 'ok';
    case 'no_beef':
      return meat === 'beef' || meat === 'unknown' ? 'block' : 'ok';
    case 'halal':
      if (meat === 'pork' || meat === 'unknown' || alcohol) return 'block';
      if (meat) return 'caution'; // land-animal meat must be halal-certified
      return 'ok';
    case 'gluten_free':
      return gluten ? 'block' : 'ok'; // 'may' (e.g. oats) fails closed
    default:
      return 'block'; // unknown rule → fail closed
  }
}

const CAUTION_NOTES: Partial<Record<DietRuleId, (name: string) => string>> = {
  halal: (n) => `Use halal-certified ${n}.`,
  vegetarian: (n) => `Traditional ${n} uses animal rennet, so pick a vegetarian-labelled one.`,
  pescatarian: (n) => `Traditional ${n} uses animal rennet, so pick a vegetarian-labelled one.`,
};

export function judgeIngredients(ids: Iterable<IngredientId>, rules: DietRuleId[]): Verdict {
  const cautions: Caution[] = [];
  for (const id of ids) {
    for (const rule of rules) {
      const v = evaluate(id, rule);
      if (v === 'block') return { blocked: true, blockedBy: rule, cautions: [] };
      if (v === 'caution') {
        const note = CAUTION_NOTES[rule]?.(INGREDIENTS[id].name) ?? `Check the label on ${INGREDIENTS[id].name}.`;
        cautions.push({ ingredient: id, rule, note });
      }
    }
  }
  return { blocked: false, cautions };
}

/** Every rule this ingredient set blocks, used for tests and the "why hidden" explanation. */
export function violatedRules(ids: Iterable<IngredientId>): DietRuleId[] {
  const list = [...ids];
  return DIET_RULES.map((r) => r.id).filter((rule) => list.some((id) => evaluate(id, rule) === 'block'));
}
